import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { createSite, deleteSite, getSites, updateSite } from '../../../services/sitesService';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';

interface Site {
  id: number;
  name: string;
  type: number;
  code: string;
  description?: string | null;
}

const Sites: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [name, setName] = useState('');
  const [type, setType] = useState('1');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Site | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadSites = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getSites();
      const items = Array.isArray(response) ? response : response?.data;
      setSites(Array.isArray(items) ? items : []);
    } catch {
      setError('Failed to load sites.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName('');
    setType('1');
    setCode('');
    setDescription('');
    setError('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    const trimmedCode = code.trim().toUpperCase();
    if (!trimmedName || !trimmedCode || !type) {
      setError('Site name, type, and code are required.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      if (editingId !== null) {
        await updateSite(editingId, { name: trimmedName, type: Number(type), code: trimmedCode, description: description.trim() });
      } else {
        await createSite({ name: trimmedName, type: Number(type), code: trimmedCode, description: description.trim() });
      }
      setSuccess(editingId !== null ? 'Site updated successfully.' : 'Site added successfully.');
      closeForm();
      await loadSites();
    } catch (err: any) {
      const validationErrors = err?.response?.data?.errors;
      const firstError = validationErrors && Object.values(validationErrors).flat()[0];
      setError(String(firstError || err?.response?.data?.message || 'Failed to save site.'));
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (site: Site) => {
    setEditingId(site.id);
    setName(site.name);
    setType(String(site.type ?? 1));
    setCode(site.code || '');
    setDescription(site.description || '');
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingId(deleteTarget.id);
      setError('');
      setSuccess('');
      await deleteSite(deleteTarget.id);
      if (editingId === deleteTarget.id) closeForm();
      setDeleteTarget(null);
      setSuccess('Site deleted successfully.');
      await loadSites();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete site.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-full overflow-auto bg-white p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Sites</h2>
          <p className="mt-1 text-sm text-gray-500">Manage sites available throughout the system.</p>
        </div>
        <button
          type="button"
          onClick={() => (showForm ? closeForm() : setShowForm(true))}
          className="flex items-center gap-2 rounded-md bg-[#e84545] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          {showForm ? <X size={16} /> : <Plus size={16} />}
          {showForm ? 'Cancel' : 'Add Site'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 rounded-lg border border-gray-200 bg-gray-50 p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <label className="block text-sm font-medium text-gray-700">
              Site Name <span className="text-red-500">*</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500"
                placeholder="Enter site name"
                maxLength={100}
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Type <span className="text-red-500">*</span>
              <input
                type="number"
                min="1"
                value={type}
                onChange={(event) => setType(event.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Code <span className="text-red-500">*</span>
              <input
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-normal uppercase outline-none focus:border-blue-500"
                placeholder="EWPGP"
                maxLength={30}
              />
            </label>
          </div>
          <label className="mt-4 block text-sm font-medium text-gray-700">
            Description
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              className="mt-1 min-h-[90px] w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 font-normal outline-none focus:border-blue-500"
              placeholder="Main head office site"
              maxLength={500}
            />
          </label>
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-[#143d7d] px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? 'Saving...' : editingId !== null ? 'Save Changes' : 'Add Site'}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full border-collapse text-left">
          <thead className="bg-gray-50 text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  Loading sites...
                </td>
              </tr>
            ) : sites.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                  No sites found.
                </td>
              </tr>
            ) : (
              sites.map((site) => (
                <tr key={site.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{site.name}</td>
                  <td className="px-4 py-3 text-gray-700">{site.type}</td>
                  <td className="px-4 py-3 text-gray-700">{site.code}</td>
                  <td className="px-4 py-3 text-gray-600">{site.description || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(site)}
                        className="rounded p-2 text-blue-700 hover:bg-blue-50"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(site)}
                        disabled={deletingId === site.id}
                        className="rounded p-2 text-red-600 hover:bg-red-50 disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Site?"
        message={`Are you sure you want to delete “${deleteTarget?.name || ''}”?`}
        confirmText="Yes, Delete"
        loadingText="Deleting..."
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={deletingId !== null}
      />
      <SuccessModal
        isOpen={!!success}
        onClose={() => setSuccess('')}
        title="Success"
        message={success}
        buttonText="OK"
        onButtonClick={() => setSuccess('')}
      />
      <ErrorModal isOpen={!!error} onClose={() => setError('')} title="Operation Failed" message={error} buttonText="OK" />
    </div>
  );
};

export default Sites;
